
import { 
  TEpDevPSortDirection,
  EpDevPApiClientBase, 
  EpDevPPluginIds,
  IEpDevPListDomainsResponse,
  EpDevPAsyncApiFormat,
  EpDevPAsyncApiVersion
} from '@solace-labs/backstage-plugin-ep-devp-common';
import { 
  IEpDevPAppCredentialsCreate,
  IEpDevPAppCredentialsCreateResponse,
  IEpDevPAppCredentialsDeleteResponse,
  IEpDevPAppListCredentialsResponse 
} from '@solace-labs/backstage-plugin-ep-devp-apps-common';
import {
  IEpDevPConsumerApp,
  IEpDevPConsumerAppContactInfoUpdate,
  IEpDevPConsumerAppContactInfoUpdateResponse,
  IEpDevPConsumerAppCreate,
  IEpDevPConsumerAppsApiClient, 
  IEpDevPConsumerAppUpdate, 
  IEpDevPListConsumerAppsResponse,
  IEpDevPListConsumerAppEventApisResponse,
  IEpDevPConsumerAppListConsumerProductRegistrationsResponse,
  IEpDevPConsumerAppConsumerProductRegistrationCreate,
  IEpDevPConsumerAppConsumerProductRegistrationCreateResponse,
  IEpDevPConsumerAppConsumerProductRegistrationDeleteResponse,
  IEpDevPConsumerAppConsumerProductRegistrationUpgrade,
  IEpDevPConsumerAppConsumerProductRegistrationUpgradeResponse, 
} from './types';
import { EpDevPConsumerAppsApiPathsFactory } from './EpDevPConsumerAppsApiFactories';

export class EpDevPConsumerAppsApiClient extends EpDevPApiClientBase implements IEpDevPConsumerAppsApiClient {

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
  }) {
    super({ ...options, pluginId: EpDevPPluginIds.EpDevPConsumerAppsPluginId });
  }

  public async import({ organizationId }:{
    organizationId: string;
  }): Promise<IEpDevPListConsumerAppsResponse> {
    const epDevPListConsumerAppsResponse: IEpDevPListConsumerAppsResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.createImportPath({
        organizationId: organizationId,
      })),
      method: 'GET',
    });    
    return epDevPListConsumerAppsResponse;
  }

  public async list({ 
    organizationId, 
    domainIds,
    pageSize,
    pageNumber,
    searchTerm,
    sortDirection,
    sortFieldName
   }:{
    organizationId: string;
    domainIds?: Array<string>;
    pageSize?: number;
    pageNumber?: number;
    searchTerm?: string;
    sortDirection?: TEpDevPSortDirection;
    sortFieldName?: string;
  }): Promise<IEpDevPListConsumerAppsResponse> {
    const epDevPListConsumerAppsResponse: IEpDevPListConsumerAppsResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.createListPath({
        organizationId: organizationId,
        domainIds: domainIds,
        pageSize: pageSize,
        pageNumber: pageNumber,
        searchTerm: searchTerm,
        sortDirection: sortDirection,
        sortFieldName: sortFieldName
      })),
      method: 'GET',      
    });
    return epDevPListConsumerAppsResponse;
  }

  public async byId({ organizationId, domainId, id, email, role }:{
    organizationId: string;
    domainId: string;
    id: string;
    email?: string;
    role?: string;
  }): Promise<IEpDevPConsumerApp> {
    const url = await this.createUrl(EpDevPConsumerAppsApiPathsFactory.createDomainByIdPath({
      organizationId: organizationId,
      domainId: domainId,
      assetId: id,
    }));
    
    // Create the body with email and role if provided
    const body = JSON.stringify({
      email: email || null,
      role: role || null
    });
    
    const epDevPConsumerApp: IEpDevPConsumerApp = await this.request({
      url: url,
      method: 'POST', // Always use POST
      body: body
    });
    
    return epDevPConsumerApp;
  }

  public async create({ organizationId, domainId, requestBody }:{
    organizationId: string;
    domainId: string; 
    requestBody: IEpDevPConsumerAppCreate;
  }): Promise<IEpDevPConsumerApp> {
    const epDevPConsumerApp: IEpDevPConsumerApp = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.managedAssetCreatePath({
        organizationId: organizationId,
        domainId: domainId,
      })),
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    return epDevPConsumerApp;
  }

  public async update({ organizationId, domainId, appId, requestBody }:{
    organizationId: string;
    domainId: string;
    appId: string;
    requestBody: IEpDevPConsumerAppUpdate;
  }): Promise<IEpDevPConsumerApp> {
    const epDevPConsumerApp: IEpDevPConsumerApp = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.managedAssetUpdatePath({
        organizationId: organizationId,
        domainId: domainId,
        assetId: appId
      })),
      method: 'PATCH',
      body: JSON.stringify(requestBody)
    });
    return epDevPConsumerApp;
  }

  public async delete({ organizationId, domainId, appId }:{
    organizationId: string;
    domainId: string;
    appId: string;
  }): Promise<void> {
    await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.managedAssetDeletePath({
        organizationId: organizationId,
        domainId: domainId,
        assetId: appId
      })),
      method: 'DELETE',
    });
  }

  public async listConsumerAppDomains({ organizationId }:{
    organizationId: string;
  }): Promise<IEpDevPListDomainsResponse> {
    const epDevPListDomainsResponse: IEpDevPListDomainsResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.listConsumerAppDomainsPath({
        organizationId: organizationId,
      })),
      method: 'GET',      
    });
    return epDevPListDomainsResponse;    
  }

  public async listCredentials({ organizationId, domainId, appId }:{
    organizationId: string;
    domainId: string;
    appId: string;
  }): Promise<IEpDevPAppListCredentialsResponse> {
    const epDevPAppListCredentialsResponse: IEpDevPAppListCredentialsResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.listCredentialsPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'GET',      
    });
    return epDevPAppListCredentialsResponse;    
  }

  public async createCredentials({ organizationId, domainId, appId, requestBody }:{
    organizationId: string;
    domainId: string;
    appId: string;
    requestBody: IEpDevPAppCredentialsCreate;
  }): Promise<IEpDevPAppCredentialsCreateResponse> {
    const epDevPAppCredentialsCreateResponse: IEpDevPAppCredentialsCreateResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.createCredentialsPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    return epDevPAppCredentialsCreateResponse;
  }

  public async deleteCredentials({ organizationId, domainId, appId, credentialsId }:{
    organizationId: string;
    domainId: string;
    appId: string;
    credentialsId: string;
  }): Promise<IEpDevPAppCredentialsDeleteResponse> {
    const epDevPAppCredentialsDeleteResponse: IEpDevPAppCredentialsDeleteResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.deleteCredentialsPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId,
        credentialsId: credentialsId
      })),
      method: 'DELETE',
    });
    return epDevPAppCredentialsDeleteResponse;
  }

  public async updateContactInfo({ organizationId, domainId, appId, requestBody }:{
    organizationId: string;
    domainId: string;
    appId: string;
    requestBody: IEpDevPConsumerAppContactInfoUpdate;
  }): Promise<IEpDevPConsumerAppContactInfoUpdateResponse> {
    const epDevPConsumerAppContactInfoUpdateResponse: IEpDevPConsumerAppContactInfoUpdateResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.updateContactInfoPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    return epDevPConsumerAppContactInfoUpdateResponse;
  }

  public async listConsumerProductRegistrations({ organizationId, domainId, appId }:{
    organizationId: string;
    domainId: string;
    appId: string;
  }): Promise<IEpDevPConsumerAppListConsumerProductRegistrationsResponse> {
    const epDevPConsumerAppListConsumerProductRegistrationsResponse: IEpDevPConsumerAppListConsumerProductRegistrationsResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.listConsumerProductRegistrationsPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'GET',      
    });
    return epDevPConsumerAppListConsumerProductRegistrationsResponse;
  }

  public async createConsumerProductRegistration({ organizationId, domainId, appId, epDevPConsumerAppConsumerProductRegistrationCreate }:{
    organizationId: string;
    domainId: string;
    appId: string;
    epDevPConsumerAppConsumerProductRegistrationCreate: IEpDevPConsumerAppConsumerProductRegistrationCreate;
  }): Promise<IEpDevPConsumerAppConsumerProductRegistrationCreateResponse> {
    const epDevPConsumerAppConsumerProductRegistrationCreateResponse: IEpDevPConsumerAppConsumerProductRegistrationCreateResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.createConsumerProductRegistrationPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'POST',
      body: JSON.stringify(epDevPConsumerAppConsumerProductRegistrationCreate)
    });
    return epDevPConsumerAppConsumerProductRegistrationCreateResponse;    
  }

  public async upgradeConsumerProductRegistration({ organizationId, domainId, appId, epDevPConsumerAppConsumerProductRegistrationUpgrade }:{
    organizationId: string;
    domainId: string;
    appId: string;
    epDevPConsumerAppConsumerProductRegistrationUpgrade: IEpDevPConsumerAppConsumerProductRegistrationUpgrade;
  }): Promise<IEpDevPConsumerAppConsumerProductRegistrationUpgradeResponse> {
    const epDevPConsumerAppConsumerProductRegistrationUpgradeResponse: IEpDevPConsumerAppConsumerProductRegistrationUpgradeResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.upgradeConsumerProductRegistrationPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId
      })),
      method: 'PATCH',
      body: JSON.stringify(epDevPConsumerAppConsumerProductRegistrationUpgrade)
    });
    return epDevPConsumerAppConsumerProductRegistrationUpgradeResponse;    
  }

  public async deleteConsumerProductRegistration({ organizationId, domainId, appId, accessRequestId }:{
    organizationId: string;
    domainId: string;
    appId: string;
    accessRequestId: string;
  }): Promise<IEpDevPConsumerAppConsumerProductRegistrationDeleteResponse> {
    const epDevPConsumerAppConsumerProductRegistrationDeleteResponse: IEpDevPConsumerAppConsumerProductRegistrationDeleteResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.deleteConsumerProductRegistrationPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId,
        accessRequestId: accessRequestId
      })),
      method: 'DELETE',
    });
    return epDevPConsumerAppConsumerProductRegistrationDeleteResponse;
  }

  public async listEventApis({ organizationId, domainId, appId, accessRequestId }:{
    organizationId: string;
    domainId: string;
    appId: string;
    accessRequestId?: string; /** if undefined, returns event apis for all accessRequestIds */
  }): Promise<IEpDevPListConsumerAppEventApisResponse> {
    const epDevPListConsumerAppEventApisResponse: IEpDevPListConsumerAppEventApisResponse = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.getListEventApisPath({
        organizationId: organizationId,
        domainId: domainId,
        appId: appId,
        accessRequestId: accessRequestId
      })),
      method: 'GET',
    });
    return epDevPListConsumerAppEventApisResponse;    
  }

  public async asyncApi({ organizationId, domainId, appId, accessRequestId, eventApiId, format, asyncApiVersion, fileNamePathForZip }:{
    organizationId: string;
    domainId: string;
    appId: string;
    accessRequestId: string;
    eventApiId: string;
    format: EpDevPAsyncApiFormat;
    asyncApiVersion: EpDevPAsyncApiVersion;
    fileNamePathForZip?: string;
  }): Promise<any> {
    const asyncApi: any = await this.request({
      url: await this.createUrl(EpDevPConsumerAppsApiPathsFactory.getAsyncApiPath({
        organizationId,
        domainId,
        appId,
        accessRequestId,
        eventApiId,
        format,
        asyncApiVersion,
        fileNamePathForZip
      })),
      method: 'GET'
    });
    return asyncApi;    
  }

}
